import React, { FC, ChangeEvent, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uniq } from 'lodash';
import { useField, useFormikContext } from 'formik';
import { Box, Divider, Grid, makeStyles, TextField, Typography } from '@material-ui/core';

import { CreateClientInvitationInput, UserType } from 'api';
import { Capability, ValidatedTextField, ValidatedSelect, CSVUpload } from 'components';
import { SingleDepartmentPicker, SingleLocationPicker } from 'components/Pickers';
import { useProtectedClient } from 'hooks';

const useStyles = makeStyles(theme => ({
  divider: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(4),
  },
}));

const InviteeForm: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { email_from_address, email_sending_domain } = useProtectedClient();
  const { setFieldValue, values } = useFormikContext<CreateClientInvitationInput>();

  const [, emailFromMeta, emailFromHelpers] = useField('email_from_address');
  const nameToUse = email_from_address?.split('@')?.[0] ?? '';
  const [fromName, setFromName] = useState(nameToUse);

  const hasFromError = !!emailFromMeta.error && !!emailFromMeta.touched;
  const fromErrorText = hasFromError ? { helperText: emailFromMeta.error } : {};

  const roles = Object.entries(UserType)
    .map(role => ({ id: role[1], title: role[0] }))
    .filter(role => role.id !== UserType.Cs);

  const endAdornment = (
    <Box mr={2}>
      <Typography>{`@${email_sending_domain}`}</Typography>
    </Box>
  );

  function handleTextAreaChange(event: ChangeEvent<HTMLTextAreaElement>) {
    const { value } = event.target;
    const emailAddresses = value.split(',').map(email => email.trim());

    setFieldValue('email_addresses', uniq(emailAddresses));
  }

  function handleValue(value: string[]) {
    return value.join(', ');
  }

  function handleFromChange(event: ChangeEvent<HTMLInputElement>) {
    const trimmed = event.target.value.trim();

    setFromName(trimmed);
    !trimmed.length
      ? emailFromHelpers.setValue(null)
      : emailFromHelpers.setValue(`${trimmed}@${email_sending_domain}`);
  }

  function renderUploadFile() {
    return (
      <Grid container>
        <Grid item xs={3}>
          <Grid item>
            <Typography variant="subtitle2" color="textPrimary">
              {t('messages:recipients')}
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={7}>
          <ValidatedTextField
            name="email_addresses"
            i18nKey="admin:emails"
            valueFn={handleValue}
            color="primary"
            TextFieldProps={{
              multiline: true,
              rows: 8,
              onChange: handleTextAreaChange,
              label: null,
              margin: 'none',
            }}
          />
          <Box paddingTop={2}>
            <CSVUpload
              s3Object={values.uploadfile}
              onS3Object={uploadfile => {
                setFieldValue('uploadfile', uploadfile);
                setFieldValue('email_addresses', []);
              }}
            />
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <Fragment>
      {renderUploadFile()}
      <Divider className={classes.divider} />
      <Grid container>
        <Grid item xs={3}>
          <Typography variant="subtitle2" color="textPrimary">
            {t('components:optionalItems')}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {t('components:not_required')}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Grid container direction="column" spacing={2}>
            <Capability name="Admin.People.Invite.Function.Department">
              <Grid item>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="subtitle2" color="textPrimary">
                      {t('user:department')}
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <SingleDepartmentPicker />
                  </Grid>
                </Grid>
              </Grid>
            </Capability>
            <Capability name="Admin.People.Invite.Function.Location">
              <Grid item>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="subtitle2" color="textPrimary">
                      {t('user:location')}
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <SingleLocationPicker />
                  </Grid>
                </Grid>
              </Grid>
            </Capability>
            <Capability name="Admin.People.Invite.Function.Role">
              <Grid item>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="subtitle2" color="textPrimary">
                      {t('admin:role')}
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <ValidatedSelect
                      disableLabel
                      margin="none"
                      name="user_type"
                      i18nKey="admin:role"
                      placeholder={t('common:select')}
                      items={roles}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Capability>
            <Capability name="Admin.People.Invite.Function.Department">
              <Divider className={classes.divider} />
            </Capability>

            <Capability name="Admin.People.Invite.Function.From">
              {!!email_sending_domain && (
                <Box paddingBottom={2}>
                  <Typography gutterBottom variant="subtitle2" color="textPrimary">
                    {t('admin:from')}
                  </Typography>
                  <TextField
                    fullWidth
                    error={!!emailFromMeta.error && !!emailFromMeta.touched}
                    value={fromName}
                    margin="none"
                    placeholder={t('admin:enterValidEmail')}
                    InputProps={{ endAdornment }}
                    onChange={handleFromChange}
                    {...fromErrorText}
                  />
                </Box>
              )}
            </Capability>

            <Box paddingBottom={2}>
              <Typography gutterBottom variant="subtitle2" color="textPrimary">
                {t('admin:subject')}
              </Typography>
              <ValidatedTextField
                color="primary"
                name="subject"
                i18nKey="admin:addASubjectLine"
                TextFieldProps={{ margin: 'none', label: null }}
              />
            </Box>
            <Box paddingBottom={2}>
              <Typography gutterBottom variant="subtitle2" color="textPrimary">
                {t('admin:message')}
              </Typography>
              <ValidatedTextField
                color="primary"
                name="message"
                i18nKey="admin:addACustomEmailMessage"
                TextFieldProps={{ multiline: true, rows: 8, label: null, margin: 'none' }}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default InviteeForm;
